import {Injectable} from "@angular/core";
import {SmartLinkPopupComponent, SmartLinkSearchResultType} from "./smart-link-popup/smart-link-popup.component";
import {SmartLinkPreviewComponent} from "./smart-link-preview/smart-link-preview.component";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {Channel} from "./models/channel.model";
import {Topic} from "./models/topic.model";
import {Attachment} from "./models/attachment.model";
import format from "date-fns/format";
import {v4 as uuidv4} from "uuid";
import {UntypedFormGroup} from "@angular/forms";
import {ChannelRepository} from "./repository/channel.repository";
import {take} from "rxjs/operators";
import {from} from "rxjs";
import {DomSanitizer} from "@angular/platform-browser";
import {DefaultCover} from "./models/default-cover.model";
import {PopupPositionService} from "./popup-position.service";
import {SmartObject} from "./smart-objects/smart-objects.service";
import {CommonUtil} from "../talk/utils/common.util";

@Injectable()
export class SmartLinkService {
  constructor(private _dialog: MatDialog,
              private _sanitizer: DomSanitizer,
              private _channelRepository: ChannelRepository,
              private _popup: PopupPositionService) {
  }

  getSmartLinkData(targetElement: Element, smartLinkData = null) {
    const getAttribute = (element, attributeName) => element.getAttribute(attributeName);
    const smartLinkPreviewData: SmartLinkData[] = !!smartLinkData ? smartLinkData : JSON.parse(decodeURIComponent(getAttribute(targetElement, "data-preview-data")));
    const getMappedSmartLinkSearchResult = (data: any) => {
      return (data.hasOwnProperty(SmartLinkErrorType.NOT_ACCESSIBLE) || data.hasOwnProperty(SmartLinkErrorType.NOT_FOUND)) ?
        data : this.getMappedSmartLinkSearchResults([data])[0];
    };
    const $smartLinkObs = smartLinkPreviewData?.map(async (smartLinkData) => {
      if (smartLinkData.previewType === SmartLinkPreviewType.CHANNEL_MAX) {
        const channel: any = await this._channelRepository.selectChannelDetail(smartLinkData?.resultData?.entityId).pipe(take(1)).toPromise();
        return {
          ...smartLinkData,
          resultData: getMappedSmartLinkSearchResult({ object_type: "channel", ...channel })
        };
      }
      if (smartLinkData.previewType === SmartLinkPreviewType.TOPIC) {
        const topic: any = await this._channelRepository.selectTopicDetail(smartLinkData?.resultData?.entityId).pipe(take(1)).toPromise();
        return {
          ...smartLinkData,
          resultData: getMappedSmartLinkSearchResult({ object_type: "topic", ...topic, description: topic?.description || topic?.description_plain })
        };
      }
      if (smartLinkData.previewType === SmartLinkPreviewType.COMMENT) {
        const comment: any = await this._channelRepository.selectCommentById(smartLinkData?.resultData?.entityId, smartLinkData?.resultData?.parentCommentId, smartLinkData?.resultData?.topicId).pipe(take(1)).toPromise();
        return {
          ...smartLinkData,
          resultData: getMappedSmartLinkSearchResult({ object_type: "comment", ...comment })
        };
      }
    });
    return from(Promise.all($smartLinkObs));
  }

  getSmartLinkPreviewRef(data, clientX: number, clientY: number, isMobileView = false): MatDialogRef<SmartLinkPreviewComponent> {
    return this._dialog.open(SmartLinkPreviewComponent, {
      width: isMobileView ? "100%" : "fit-content",
      height: isMobileView ? "100%" : "fit-content",
      backdropClass: "transparent-backdrop",
      panelClass: isMobileView ? "smartlink-preview-container-mobile" : "smartlink-preview-container",
      position: isMobileView ? {
        left: "0px",
        top: "0px"
      } :  this._popup.getPopupPosition({ width: 760, height: 440 }, clientX, clientY),
      data
    });
  }

  getSmartLinkPopupRef(
     selectedText: string,
     clientX,
     clientY,
     getOnlyData = false,
     skipFirstStep = false,
     searchResult = null,
     selectedSmartLinkData = null,
     alreadyLinkedObjects = null,
     isMobileView = false
    ): MatDialogRef<SmartLinkPopupComponent> {
    return this._dialog.open(SmartLinkPopupComponent, {
      width: isMobileView ? "100%" : "760px",
      height: isMobileView ? "100%" : "fit-content",
      backdropClass: "transparent-backdrop",
      panelClass: isMobileView ? "smartlink-popup-container-mobile" : "smartlink-popup-container",
      data: { selectedText, getOnlyData, skipFirstStep, selectedSearchResult: searchResult, selectedSmartLinkData, alreadyLinkedObjects, isMobileView },
      position: isMobileView ? {
        left: "0px",
        top: "0px"
      } : this._popup.getPopupPosition({ width: 760, height: 560 }, clientX, clientY)
    });
  }

  constructSmartLinkData(form: UntypedFormGroup, smartLinkName: string, selectedResult, getOnlyData = false) {
    const previewType: SmartLinkPreviewType = selectedResult.resultType === SmartLinkSearchResultType.CHANNEL ?
      SmartLinkPreviewType.CHANNEL_MAX : selectedResult.resultType;
    const resultData = { entityId: selectedResult.id };
    if (previewType === SmartLinkPreviewType.TOPIC) {
      resultData["metadata"] = { channelId: selectedResult.channel_id };
    } else if (previewType === SmartLinkPreviewType.COMMENT) {
      resultData["metadata"] = { channelId: selectedResult.channel_id, topicId: selectedResult.topic_id };
      if (selectedResult?.parent_comment_id) {
        resultData["metadata"]["parentCommentId"] = selectedResult?.parent_comment_id;
      }
    }

    const data: { type: "smartlink", smartlinkId: string, previewData: SmartLinkData[] } = {
      type: "smartlink",
      smartlinkId: uuidv4(),
      previewData: [{
        previewType,
        resultType: selectedResult.resultType,
        linkText: form?.value?.title,
        description: form?.value?.description,
        searchQuery: form?.value?.search,
        resultData: getOnlyData ? { ...selectedResult, description: selectedResult?.description || selectedResult?.description_plain } : resultData
      }]
    };

    if (getOnlyData) {
      return data;
    }
    const element = document.createElement("s");
    element.contentEditable = "false";
    element.classList.add("smartlink-text");
    Object.entries(data).forEach(entry => {
      element.dataset[entry[0]] = typeof entry[1] === "object" ? encodeURIComponent(JSON.stringify(entry[1])) : entry[1];
    });
    element.textContent = smartLinkName;
    return element;
  }

  constructMultiSmartLinkData(objects, smartLinkName, getOnlyData = false) {
    const data: { type: "smartlink", smartlinkId: string, previewData: SmartLinkData[] } = {
      type: "smartlink",
      smartlinkId: uuidv4(),
      previewData: objects.map(object => {
        const previewType = object.resultType === SmartLinkSearchResultType.CHANNEL ? SmartLinkPreviewType.CHANNEL_MAX : object.resultType;
        const resultData = { entityId: object.id };
        if (previewType === SmartLinkPreviewType.TOPIC) {
          resultData["metadata"] = { channelId: object.channel_id };
        } else if (previewType === SmartLinkPreviewType.COMMENT) {
          resultData["metadata"] = { channelId: object.channel_id, topicId: object.topic_id };
          if (object?.parent_comment_id) {
            resultData["metadata"]["parentCommentId"] = object?.parent_comment_id;
          }
        }
        return {
          previewType,
          resultType: object.resultType,
          linkText: smartLinkName,
          description: "",
          searchQuery: "",
          resultData: getOnlyData ? { ...object, description: object?.description || object?.description_plain } : resultData
        };
      })
    };

    if (getOnlyData) {
      return data;
    }

    const element = document.createElement("s");
    element.contentEditable = "false";
    element.classList.add("smartlink-text");
    Object.entries(data).forEach(entry => {
      element.dataset[entry[0]] = typeof entry[1] === "object" ? encodeURIComponent(JSON.stringify(entry[1])) : entry[1];
    });
    element.textContent = smartLinkName;
    return element;
  }

  getMappedSmartLinkPreviewData(searchResult: Channel | Topic | any) {
    switch (searchResult?.resultType) {
      case SmartLinkSearchResultType.CHANNEL: {
        return {
          channelFullData: {
            ...searchResult,
            tags: (searchResult?.tags || []).map(tag => tag?.name)
          }
        };
      }
      case SmartLinkSearchResultType.TOPIC: {
        return {
          topicData: {
            ...searchResult,
            description: this._sanitizer.bypassSecurityTrustHtml(searchResult?.description),
            heroAttachmentThumbnailUrl: (searchResult.attachments as Attachment[])?.filter(attachment => attachment?.is_header || attachment?.is_default_thumbnail)?.[0]?.thumbnail_url || searchResult?.default_cover_url || searchResult?.default_cover
          },
        };
      }
      case SmartLinkSearchResultType.COMMENT: {
        return {
          commentFullData: {
            ...searchResult,
            updated_on: format(Date.parse(searchResult?.updated_on), "dd/MM/yy HH:mm").split(" ").join(" at "),
            description: this._sanitizer.bypassSecurityTrustHtml(searchResult?.description),
            topic_name: searchResult?.topic_subject
          }
        };
      }
      default: return searchResult;
    }
  }
  getMappedSmartLinkSearchResults(searchResults: any[]){
    return searchResults.map((searchResult) => {
      switch (searchResult["object_type"]) {
        case "topic": {
          // date is formated according to timezone offset
          return {
            ...searchResult,
            heroAttachmentThumbnailUrl: CommonUtil.getAttachmentLocalAPIURL((searchResult?.attachments as Attachment[]).filter(attachment => attachment.is_header || attachment?.is_default_thumbnail)?.[0]?.thumbnail_url) || searchResult.default_cover_url || searchResult?.default_cover,
            default_cover_url: this.getDefaultCover(searchResult?.default_cover || null),
            resultType: SmartLinkSearchResultType.TOPIC,
            date: format(new Date(searchResult?.created_on).setMinutes(new Date(searchResult?.created_on).getMinutes() + new Date().getTimezoneOffset()),"dd.MM.yyyy"),
            topic_name: searchResult?.subject,
            description: searchResult?.description || searchResult?.description_plain,
            channel_name: searchResult?.channel_name
          };
        }
        case "channel": {
          return {
            ...searchResult,
            resultType: SmartLinkSearchResultType.CHANNEL,
            date: format(new Date(searchResult?.created_on).setMinutes(new Date(searchResult?.created_on).getMinutes() + new Date().getTimezoneOffset()),"dd.MM.yyyy"),
            channel_name: searchResult?.name,
            description: searchResult?.description,
            topics_count: searchResult?.topics_count,
            author_name: searchResult?.author?.name || searchResult?.author?.fullName
          };
        }
        case "comment": {
          return {
            ...searchResult,
            resultType: SmartLinkSearchResultType.COMMENT,
            date: format(new Date(searchResult?.created_on).setMinutes(new Date(searchResult?.created_on).getMinutes() + new Date().getTimezoneOffset()),"dd.MM.yyyy"),
            channel_name: searchResult?.channel_name,
            topic_name: searchResult?.topic_subject,
            author_name: searchResult?.author?.name || searchResult?.author?.fullName,
            description: searchResult?.comments
          };
        }
        default: return searchResult;
      }
    });
  }

  private getDefaultCover(coverName?: string) {
    let defaultCover: DefaultCover;
    if (!!coverName) {
      this._channelRepository.getDefaultCoverByCoverName(coverName).pipe(take(1)).subscribe(res => defaultCover = res?.[0]);
    } else {
      this._channelRepository.getDefaultCovers().pipe(take(1)).subscribe(res => defaultCover = res?.[0]);
    }
    return defaultCover?.url;
  }

  removeSmartObjectIndicesFromString(text) {
    if (!text.match(/(\[\d*?\]<\/s>)/g) || text.match(/(\[\d*?\]<\/s>)/g)?.length === 0) {
      return text;
    } else {
      return this.removeSmartObjectIndicesFromString(text.replace(/(\[\d*?\]<\/s>)/g, "</s>"));
    }
  }

  replaceHTMLQuillContent(data, popupConfig, editor) {
    // let {preContent, postContent} = popupConfig;
    // preContent = this.removeSmartObjectIndicesFromString(preContent);
    // postContent = this.removeSmartObjectIndicesFromString(postContent);
    // let updatedQuillContent = preContent + data + postContent;
    // updatedQuillContent = this.removeSmartObjectIndicesFromString(updatedQuillContent);
    let updatedQuillContent = this.replaceQuillHTML(data, editor, popupConfig);
    updatedQuillContent = this.getOldAttributes(editor).reduce(
      (quillContent: string, oldSmartLinkData: { original: string, replaceWith: string }) => quillContent.replace(oldSmartLinkData.replaceWith, oldSmartLinkData.original),
      updatedQuillContent
    );
    return updatedQuillContent;
  }

  replaceQuillHTML(element: string, editor: any, popupConfig: any) {
    const range = editor.selection.savedRange;
    if (range && range.length === 0 &&  popupConfig?.savedRange) {
      range.index = popupConfig.savedRange.index;
      range.length = popupConfig.savedRange.length;
    }
    editor.deleteText(range.index, range.length);
    editor.clipboard.dangerouslyPasteHTML(range.index, element);
    const elementDoc = new DOMParser().parseFromString(element, "text/html");
    let nodeToAdd = elementDoc.querySelector<HTMLElement>("s[data-type=\"remark\"]");
    if (!nodeToAdd) {
      nodeToAdd = elementDoc.querySelector<HTMLElement>("s[data-type=\"SMART_OBJECTS\"]");
    }
    const initialHTML = editor.root.innerHTML;
    if (nodeToAdd) {
      const parentNodeId = nodeToAdd.dataset.uid;
      const htmlDoc = new DOMParser().parseFromString(initialHTML, "text/html");
      htmlDoc.querySelectorAll<HTMLElement>(`s[data-remark-id=${parentNodeId}]`).forEach(e => e.remove());
      const nodeToReplace = htmlDoc.querySelector(`s[data-uid=${parentNodeId}]`);
      if (nodeToReplace) {
        nodeToReplace.replaceWith(nodeToAdd);
        return htmlDoc.body.innerHTML;
      }
    }
    return editor.root.innerHTML;
  }

  private getOldAttributes(editor): {original: string, replaceWith: string}[] {
    const smartLinkNodes = editor.root.querySelectorAll("s[data-type=\"smartlink\"]");
    const keywordNodes = editor.root.querySelectorAll("s[data-type=\"keyword\"]");
    const stickyNoteNodes = editor.root.querySelectorAll("s[data-type=\"sticky-note\"]");
    const remarkNodes = editor.root.querySelectorAll("s[data-type=\"remark\"]");
    const smartObjectsNodes = editor.root.querySelectorAll("s[data-type=\"SMART_OBJECTS\"]");
    const allNodes = [...smartLinkNodes, ...keywordNodes, ...stickyNoteNodes, ...remarkNodes, ...smartObjectsNodes];
    const data = [];

    const removeAllAttributes = (node: any) => {
      [...node.attributes].forEach(attribute => node.removeAttribute(attribute.name));
      return node;
    };
    for (let i = 0; i < allNodes.length; i++) {
      const mappedNode = removeAllAttributes(allNodes[i].cloneNode(true));
      if (!!mappedNode?.querySelector("s[data-type=\"remark-index\"]")) {
        mappedNode.textContent = mappedNode?.childNodes?.[0]?.textContent + mappedNode?.childNodes?.[1]?.textContent;
      }
      data.push({ original: allNodes[i].outerHTML, replaceWith: mappedNode.outerHTML });
    }

    return data;
  }

  getMappedSmartLinkMetaData(data) {
    return data.map(datum => {
      const data = {...datum};
      const resultData = {entityId: datum?.resultData?.id, entityType: datum?.resultData?.resultType};

      if (datum?.resultData?.resultType === SmartLinkPreviewType.TOPIC) {
        resultData["channelId"] = datum?.resultData?.channel_id;
      }

      if (datum?.resultData?.resultType === SmartLinkPreviewType.COMMENT) {
        resultData["topicId"] = datum?.resultData?.topic_id;
        resultData["channelId"] = datum?.resultData?.channel_id;
      }

      return {
        ...data,
        resultData
      };
    });
  }

  addSmartLinkToExistingSmartString(element, oldData) {
    const linkText = element.textContent;

    const newElement = document.createElement("s");
    newElement.dataset["type"] = SmartObject.SMART_OBJECTS;
    newElement.dataset[SmartObject.SMART_LINK.toLowerCase()] = "true";

    if (Object.keys(oldData)?.includes("highlightData")) {
      newElement.dataset[SmartObject.HIGHLIGHT.toLowerCase()] = "true";
    }

    if (Object.keys(oldData)?.includes("stickyNotesData")) {
      newElement.setAttribute("data-sticky-note", "true");
    }

    const newData = {
      ...oldData
    };
    newElement.setAttribute("data-preview-data", encodeURIComponent(JSON.stringify(newData)));
    newElement.contentEditable = "false";
    newElement.textContent = linkText;

    return newElement;
  }
}



export enum SmartLinkPreviewType {
  BROADCAST = "BROADCAST",
  CHANNEL_MIN = "CHANNEL_MIN",
  CHANNEL_MAX = "CHANNEL_MAX",
  TOPIC = "TOPIC",
  COMMENT = "COMMENT"
}

export enum FilterType {
  GLOBAL_UNREAD = "GLOBAL_UNREAD",
  GLOBAL_STARRED = "GLOBAL_STARRED",
  GLOBAL_ARCHIVED = "GLOBAL_ARCHIVED",
  GLOBAL_DATE_RANGE = "GLOBAL_DATE_RANGE",

  CHANNEL_TYPE = "CHANNEL_TYPE",
  CHANNEL_USER = "CHANNEL_USER",
  CHANNEL_AUTHOR = "CHANNEL_AUTHOR",

  TOPIC_TYPE = "TOPIC_TYPE",
  TOPIC_PRIVACY = "TOPIC_PRIVACY",
  TOPIC_SUBJECT = "TOPIC_SUBJECT",
  TOPIC_USER = "TOPIC_USER",
  TOPIC_AUTHOR = "TOPIC_AUTHOR",

  COMMENT_CHANNEL = "COMMENT_CHANNEL",
  COMMENT_TOPIC = "COMMENT_TOPIC",
  COMMENT_AUTHOR = "COMMENT_AUTHOR",
}

export enum SmartLinkCategories {
  EVERYWHERE = "everywhere",
  CHANNELS = "channels",
  TOPICS = "topics",
  COMMENTS = "comments",
  CHATS = "chats",
  FILES = "files",
  BROADCAST = "broadcast"
}

export interface SmartLinkData {
  previewType: SmartLinkPreviewType;
  resultType: SmartLinkSearchResultType;
  linkText?: string;
  description?: string;
  searchQuery?: string;
  resultData: any;
}

export enum SmartLinkActions {
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  ADD_REFERENCE = "ADD_REFERENCE",
  ADD_STICKY_NOTE = "ADD_STICKY_NOTE",
  ADD_HIGHLIGHT = "ADD_HIGHLIGHT"
}

export enum SmartLinkErrorType {
  NOT_ACCESSIBLE = "noAccess",
  NOT_FOUND = "notFound"
}

export const DEFAULT_CHANNEL_THUMBNAIL_URL = "data:image/png;base64,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";
